import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Briefcase, Code, Sparkles } from 'lucide-react';
import { profileData } from '../data/mock';

const About = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const stats = [
    { label: 'Projects Delivered', value: '5+', icon: Code },
    { label: 'Current Role', value: 'Software Engineer', icon: Briefcase },
    { label: 'Location', value: 'India', icon: MapPin },
  ];

  return (
    <section
      id="about"
      ref={sectionRef}
      className="py-32 bg-[#0f0f10] relative overflow-hidden"
    >
      {/* Background glow */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-indigo-600/10 rounded-full blur-[150px]" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Column - Text Content */}
          <div>
            <div className="overflow-hidden">
              <p
                className={`text-indigo-400 text-sm uppercase tracking-widest mb-4 transition-all duration-700 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                }`}
                style={{ fontFamily: 'Figtree, sans-serif' }}
              >
                About Me
              </p>
            </div>
            
            <div className="overflow-hidden">
              <h2
                className={`text-4xl md:text-5xl font-light text-white mb-8 transition-all duration-700 delay-100 ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
                }`}
                style={{ fontFamily: 'Figtree, sans-serif' }}
              >
                Building digital
                <br />
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">experiences</span>
              </h2>
            </div>

            <p
              className={`text-gray-400 text-lg leading-relaxed mb-8 transition-all duration-700 delay-200 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
              style={{ fontFamily: 'Figtree, sans-serif' }}
            >
              {profileData.about}
            </p>

            <div
              className={`flex items-center gap-6 transition-all duration-700 delay-300 ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            >
              <div className="flex items-center gap-2 text-gray-400">
                <MapPin size={18} className="text-indigo-400" />
                <span style={{ fontFamily: 'Figtree, sans-serif' }}>{profileData.location}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Briefcase size={18} className="text-indigo-400" />
                <span style={{ fontFamily: 'Figtree, sans-serif' }}>GoodWorkLabs</span>
              </div>
            </div>
          </div>

          {/* Right Column - Stats Cards */}
          <div className="grid gap-6">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className={`group bg-white/[0.03] border border-white/[0.05] p-8 rounded-2xl hover:bg-white/[0.05] hover:border-indigo-500/30 transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_0_30px_rgba(99,102,241,0.1)] ${
                  isVisible ? 'translate-x-0 opacity-100' : 'translate-x-12 opacity-0'
                }`}
                style={{ transitionDelay: `${300 + index * 100}ms` }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p
                      className="text-3xl font-light text-white mb-2 group-hover:text-indigo-400 transition-colors duration-500"
                      style={{ fontFamily: 'Figtree, sans-serif' }}
                    >
                      {stat.value}
                    </p>
                    <p
                      className="text-gray-500 text-sm"
                      style={{ fontFamily: 'Figtree, sans-serif' }}
                    >
                      {stat.label}
                    </p>
                  </div>
                  <div className="w-12 h-12 bg-indigo-500/10 rounded-full flex items-center justify-center group-hover:bg-indigo-500/20 transition-colors duration-500">
                    <stat.icon size={24} className="text-indigo-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
